import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { useSelector, useDispatch, useStore } from "@grandvisionhq/state";
import { useRestoreCartShippingMethod } from "../../../../../../../../hooks/carts/hooks/carts/use-restore-cart-shipping-method.js";
import { selectShippingMethod, selectServiceStore, selectStore, selectPickupPoint } from "../../../../../../../StepShipping/actions.js";
import { getSelectedShippingMethod, getSelectedStore, getSelectedPickupPoint } from "../../../../../../../StepShipping/selectors.js";
import { useShippingMethodSelection } from "../../../../../hooks/StepShipping/modules/ShippingMethods/hooks/use-shipping-method-selection.js";
import { useShippingMethodsList } from "../../../../../hooks/StepShipping/modules/ShippingMethods/hooks/use-shipping-methods-list.js";
import { shippingMethodsSuccess, shippingMethodsFailed } from "../../../../../state/StepShipping/modules/ShippingMethods/state/actions.js";
import { isPickupStore, isPickupPoint } from "../../../../../../../../utils/utils/shipping-method.js";
import { ShippingMethod } from "../../../../../../../../models/step-shipping/models/step-shipping/shipping-methods.js";
import { ShippingMethodsListContainer } from "../../../../../containers/StepShipping/modules/ShippingMethods/containers/ShippingMethodsListContainer.js";
import { SelectedShippingMethodContainer } from "../../../../../containers/StepShipping/modules/ShippingMethods/containers/SelectedShippingMethodContainer.js";
import { useCheckoutSettings } from "../../../../../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const ShippingMethods = ({ shippingMethodsAsides }) => {
  const { shipping, features } = useCheckoutSettings();
  const selectedShippingMethod = useSelector(getSelectedShippingMethod);
  const dispatch = useDispatch();
  const { cart } = Context.useCart();
  const store = useStore();
  const selectedStore = useSelector(getSelectedStore);
  const selectedPickupPoint = useSelector(getSelectedPickupPoint);
  const { restoreShippingMethod } = useRestoreCartShippingMethod();
  const onShippingMethodsLoadCompleted = React.useCallback(
    (shippingMethodList2) => {
      const selectedShippingMethod2 = getSelectedShippingMethod(store.getState());
      const isSelectedShippingMethodValid = shippingMethodList2.find(
        (method) => selectedShippingMethod2?.method == method.key
      );
      if (selectedShippingMethod2?.method && !isSelectedShippingMethodValid) {
        dispatch(
          selectShippingMethod({
            shippingMethod: {
              method: void 0,
              isValid: false,
              isTouched: true
            }
          })
        );
      } else if (cart && cart?.shippingMethod.key !== selectedShippingMethod2?.method) {
        if (cart?.shippingMethod.key) {
          dispatch(
            selectShippingMethod({
              shippingMethod: {
                method: cart?.shippingMethod.key,
                isValid: true,
                isTouched: false
              }
            })
          );
        } else {
          const defaultShippingMethod = shippingMethodList2.find((method) => method.isDefault);
          if (defaultShippingMethod) {
            dispatch(
              selectShippingMethod({
                shippingMethod: {
                  method: defaultShippingMethod.key,
                  isValid: true,
                  isTouched: false
                }
              })
            );
          }
        }
      }
      dispatch(shippingMethodsSuccess({ result: shippingMethodList2 }));
    },
    [cart, dispatch, store]
  );
  const onShippingMethodLoadError = React.useCallback(() => {
    dispatch(shippingMethodsFailed({ message: "Unable to load shipping methods" }));
  }, [dispatch]);
  const { shippingMethodList, reloadShippingMethods, isLoading, error } = useShippingMethodsList(
    onShippingMethodsLoadCompleted,
    onShippingMethodLoadError
  );
  const onError = React.useCallback(() => {
    restoreShippingMethod();
    reloadShippingMethods();
  }, [reloadShippingMethods, restoreShippingMethod]);
  const handleOnShippingMethodSelectSuccess = React.useCallback(
    (shippingMethod) => {
      if (features?.isServiceStoreEnabled && isPickupStore(shippingMethod) && selectedStore) {
        const serviceStore = {
          globalStoreId: selectedStore.globalStoreId,
          name: selectedStore.name,
          streetName: selectedStore.streetName,
          streetNumber: selectedStore.streetNumber,
          town: selectedStore.town
        };
        dispatch(selectServiceStore({ serviceStore }));
      }
    },
    [dispatch, features?.isServiceStoreEnabled, selectedStore]
  );
  const { handleOnShippingMethodSelected } = useShippingMethodSelection({
    shippingMethodList: shippingMethodList || [],
    onError,
    onSuccess: handleOnShippingMethodSelectSuccess
  });
  const onShippingMethodSelect = React.useCallback(
    (method) => {
      dispatch(
        selectShippingMethod({
          shippingMethod: {
            method,
            isValid: true,
            isTouched: true
          }
        })
      );
      switch (method) {
        case ShippingMethod.Pickup:
          if (selectedStore) {
            handleOnShippingMethodSelected(method, selectedStore.globalStoreId);
          }
          break;
        case ShippingMethod.PickupPoint:
          if (selectedPickupPoint) {
            handleOnShippingMethodSelected(method);
          }
          break;
        default:
          handleOnShippingMethodSelected(method);
          break;
      }
    },
    [dispatch, handleOnShippingMethodSelected, selectedPickupPoint, selectedStore]
  );
  const onStoreSelect = React.useCallback(
    async (store2) => {
      const { globalStoreId } = store2;
      if (selectedShippingMethod?.method && isPickupStore(selectedShippingMethod?.method) && !isPickupStore(cart?.shippingMethod.key)) {
        dispatch(selectStore({ store: store2 }));
        await handleOnShippingMethodSelected(selectedShippingMethod?.method, globalStoreId);
      }
      if (cart?.shippingMethod && isPickupStore(cart.shippingMethod.key) && selectedStore?.globalStoreId !== globalStoreId) {
        dispatch(selectStore({ store: store2 }));
        await handleOnShippingMethodSelected(cart?.shippingMethod.key, globalStoreId);
      }
    },
    [
      cart?.shippingMethod,
      dispatch,
      handleOnShippingMethodSelected,
      selectedShippingMethod?.method,
      selectedStore?.globalStoreId
    ]
  );
  const onPickupPointSelect = React.useCallback(
    async (pickupPoint) => {
      if (!pickupPoint) {
        return;
      }
      dispatch(selectPickupPoint({ pickupPoint }));
      if (selectedShippingMethod?.method && !isPickupPoint(cart?.shippingMethod.key)) {
        await handleOnShippingMethodSelected(selectedShippingMethod?.method);
      }
    },
    [
      cart?.shippingMethod.key,
      dispatch,
      handleOnShippingMethodSelected,
      selectedShippingMethod?.method
    ]
  );
  const handleClose = () => {
    restoreShippingMethod();
  };
  React.useEffect(() => {
    if (!selectedShippingMethod?.isTouched && selectedShippingMethod?.isValid && shipping?.explicitShippingMethod) {
      dispatch(
        selectShippingMethod({
          shippingMethod: { method: void 0, isValid: false, isTouched: false }
        })
      );
    }
  }, [
    dispatch,
    selectedShippingMethod?.isTouched,
    selectedShippingMethod?.isValid,
    shipping?.explicitShippingMethod
  ]);
  if (!shippingMethodList) return null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "checkout-shipping-methods" }, /* @__PURE__ */ React.createElement(
    ShippingMethodsListContainer,
    {
      asides: shippingMethodsAsides,
      selectedShippingMethod,
      shippingMethodList,
      handleOnShippingMethodSelected: onShippingMethodSelect,
      isLoading,
      error
    }
  )), /* @__PURE__ */ React.createElement(
    SelectedShippingMethodContainer,
    {
      selectedShippingMethod: selectedShippingMethod?.method,
      onStoreSelect,
      selectedStore,
      onPickupPointSelect,
      selectedPickupPoint,
      onShippingModalClose: handleClose
    }
  ));
};
export {
  ShippingMethods
};
//# sourceMappingURL=ShippingMethods.js.map
